// Zustand Store for AI Chat System
// Modern state management without Context API boilerplate

import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { generateUploadFilename } from '@/utils/imageProcessing';
import type {
  ChatState,
  AIConversation,
  AIMessage,
  AIDocument,
  AgentType,
  ChatError,
  StreamingMessage,
  SearchResult,
  ImageAttachment
} from '@/types/chat';
import { AGENT_CONFIGS } from '@/config/agents';

const log = createComponentLogger('ChatStore', 'chat-state');

// Storage bucket for chat images
const CHAT_IMAGES_BUCKET = 'chat-images';

// Track active Realtime subscriptions (outside Zustand state for performance)
let activeRealtimeChannel: ReturnType<typeof supabase.channel> | null = null;
let subscribedConversationId: string | null = null;

// ============================================================================
// Zustand Store
// ============================================================================

export const useChatStore = create<ChatState>((set, get) => ({
  // ============================================================================
  // State
  // ============================================================================

  conversations: [],
  activeConversationId: null,
  isLoadingConversations: false,

  messages: {},
  isLoadingMessages: false,

  streamingMessage: null,

  isSending: false,
  error: null,

  documents: {},

  // ============================================================================
  // Actions - Conversations
  // ============================================================================

  loadConversations: async () => {
    set({ isLoadingConversations: true, error: null });
    log.info('Loading conversations');

    try {
      // First, get conversations
      const { data: conversations, error: convError } = await supabase
        .from('ai_internal_chat_conversations')
        .select('*')
        .eq('archived', false)
        .gt('message_count', 0)
        .order('updated_at', { ascending: false });

      if (convError) {
throw convError;
}

      // Then, get token counts for each conversation
      if (conversations && conversations.length > 0) {
        const conversationIds = conversations.map(c => c.id);

        const { data: tokenData, error: tokenError } = await supabase
          .from('ai_internal_chat_messages')
          .select('conversation_id, tokens_input, tokens_output')
          .in('conversation_id', conversationIds);

        if (!tokenError && tokenData) {
          // Calculate total tokens per conversation
          const tokenMap = tokenData.reduce((acc, msg) => {
            if (!acc[msg.conversation_id]) {
              acc[msg.conversation_id] = 0;
            }
            acc[msg.conversation_id] += (msg.tokens_input || 0) + (msg.tokens_output || 0);
            return acc;
          }, {} as Record<string, number>);

          // Add token counts to conversations
          conversations.forEach(conv => {
            (conv as any).tokens_total = tokenMap[conv.id] || 0;
          });
        }
      }

      set({
        conversations: conversations || [],
        isLoadingConversations: false
      });

      log.info('Conversations loaded', { count: conversations?.length || 0 });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Failed to load conversations', { errorMessage }, error as Error);
      set({
        error: {
          code: 'DATABASE_ERROR',
          message: `Failed to load conversations: ${errorMessage}`,
          details: { errorMessage }
        },
        isLoadingConversations: false
      });
    }
  },

  createConversation: async (agentType: AgentType, title?: string) => {
    // Race condition guard - prevent duplicate creations
    if (get().isSending) {
      log.warn('Conversation creation already in progress, ignoring duplicate request');
      return get().activeConversationId || '';
    }

    set({ isSending: true, error: null });
    log.info('Creating conversation', { agentType, title });

    try {
      // Get current user
      const {
        data: { user }
      } = await supabase.auth.getUser();
      if (!user) {
throw new Error('Not authenticated');
}

      // Get user's organization
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (userError) {
        throw new Error(`User lookup failed: ${userError.message}`);
      }
      if (!userData) {
throw new Error('User profile not found');
}

      // Create conversation
      const { data: conversation, error } = await supabase
        .from('ai_internal_chat_conversations')
        .insert({
          user_id: user.id,
          organization_id: userData.organization_id,
          agent_type: agentType,
          title: title || null
        })
        .select()
        .single();

      if (error) {
throw error;
}

      // Add to state
      set((state) => ({
        conversations: [conversation, ...state.conversations],
        activeConversationId: conversation.id,
        isSending: false
      }));

      log.info('Conversation created', { conversationId: conversation.id });

      return conversation.id;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Failed to create conversation', { agentType, errorMessage }, error as Error);
      set({
        error: {
          code: 'DATABASE_ERROR',
          message: `Failed to create conversation: ${errorMessage}`,
          details: { errorMessage }
        },
        isSending: false
      });
      throw error;
    }
  },

  setActiveConversation: (conversationId: string | null) => {
    log.debug('Setting active conversation', { conversationId });
    set({ activeConversationId: conversationId });

    // Load messages for this conversation if not already loaded
    if (conversationId && !get().messages[conversationId]) {
      get().loadMessages(conversationId);
    }

    // Manage Realtime subscriptions
    // Unsubscribe from previous conversation if different
    if (subscribedConversationId && subscribedConversationId !== conversationId) {
      if (activeRealtimeChannel) {
        log.debug('Unsubscribing from previous conversation', { subscribedConversationId });
        supabase.removeChannel(activeRealtimeChannel);
        activeRealtimeChannel = null;
      }
      subscribedConversationId = null;
    }

    // Subscribe to new conversation for real-time updates
    if (conversationId && conversationId !== subscribedConversationId) {
      log.debug('Subscribing to conversation for realtime', { conversationId });
      subscribedConversationId = conversationId;

      activeRealtimeChannel = supabase
        .channel(`messages:${conversationId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'ai_internal_chat_messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          (payload) => {
            const newMessage = payload.new as AIMessage;
            log.debug('Realtime message received', { messageId: newMessage.id, conversationId });

            // Add message to state if not already present
            set((state) => {
              const existing = state.messages[conversationId] || [];
              // Avoid duplicates (check by ID)
              if (existing.some(m => m.id === newMessage.id)) {
                return state;
              }
              return {
                messages: {
                  ...state.messages,
                  [conversationId]: [...existing, newMessage]
                }
              };
            });
          }
        )
        .subscribe((status) => {
          log.debug('Realtime subscription status', { status, conversationId });
        });
    }
  },

  deleteConversation: async (conversationId: string) => {
    log.info('Deleting conversation', { conversationId });

    try {
      const { error } = await supabase
        .from('ai_internal_chat_conversations')
        .delete()
        .eq('id', conversationId);

      if (error) {
throw error;
}

      // Remove from state
      set((state) => ({
        conversations: state.conversations.filter((c) => c.id !== conversationId),
        activeConversationId:
          state.activeConversationId === conversationId
            ? null
            : state.activeConversationId,
        messages: Object.fromEntries(
          Object.entries(state.messages).filter(([key]) => key !== conversationId)
        )
      }));

      log.info('Conversation deleted', { conversationId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Failed to delete conversation', { conversationId, errorMessage }, error as Error);
      set({
        error: {
          code: 'DATABASE_ERROR',
          message: `Failed to delete conversation: ${errorMessage}`,
          details: { errorMessage }
        }
      });
    }
  },

  archiveConversation: async (conversationId: string) => {
    log.info('Archiving conversation', { conversationId });

    try {
      const { error } = await supabase
        .from('ai_internal_chat_conversations')
        .update({ archived: true, is_active: false })
        .eq('id', conversationId);

      if (error) {
throw error;
}

      // Remove from active conversations
      set((state) => ({
        conversations: state.conversations.filter((c) => c.id !== conversationId),
        activeConversationId:
          state.activeConversationId === conversationId
            ? null
            : state.activeConversationId
      }));

      log.info('Conversation archived', { conversationId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Failed to archive conversation', { conversationId, errorMessage }, error as Error);
      set({
        error: {
          code: 'DATABASE_ERROR',
          message: `Failed to archive conversation: ${errorMessage}`,
          details: { errorMessage }
        }
      });
    }
  },

  // ============================================================================
  // Actions - Messages
  // ============================================================================

  loadMessages: async (conversationId: string) => {
    set({ isLoadingMessages: true, error: null });
    log.info('Loading messages', { conversationId });

    try {
      const { data: messages, error } = await supabase
        .from('ai_internal_chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
throw error;
}

      // Merge fetched messages with any that arrived via realtime during fetch
      // This prevents race conditions where realtime messages are lost
      set((state) => {
        const existing = state.messages[conversationId] || [];
        const fetched = messages || [];

        // Merge: start with fetched, add any existing not in fetched
        const merged = [...fetched];
        for (const msg of existing) {
          if (!merged.some(m => m.id === msg.id)) {
            merged.push(msg);
          }
        }

        // Re-sort by created_at to maintain order
        merged.sort((a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        return {
          messages: {
            ...state.messages,
            [conversationId]: merged
          },
          isLoadingMessages: false
        };
      });

      log.info('Messages loaded', { conversationId, count: messages?.length || 0 });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Failed to load messages', { conversationId, errorMessage }, error as Error);
      set({
        error: {
          code: 'DATABASE_ERROR',
          message: `Failed to load messages: ${errorMessage}`,
          details: { errorMessage }
        },
        isLoadingMessages: false
      });
    }
  },

  sendMessage: async (content: string, images?: File[], conversationId?: string) => {
    set({ isSending: true, error: null });

    const activeId = conversationId || get().activeConversationId;
    if (!activeId) {
      log.error('No active conversation');
      set({
        error: {
          code: 'INVALID_REQUEST',
          message: 'No active conversation'
        },
        isSending: false
      });
      return;
    }

    log.info('Sending message', {
      conversationId: activeId,
      contentLength: content.length,
      imageCount: images?.length || 0
    });

    try {
      // Get current user for image uploads
      const {
        data: { user }
      } = await supabase.auth.getUser();
      if (!user) {
throw new Error('Not authenticated');
}

      // Upload images to storage if any
      const imageAttachments: ImageAttachment[] = [];
      const imageUrls: string[] = [];

      if (images && images.length > 0) {
        log.info('Uploading images', { count: images.length });

        for (const image of images) {
          try {
            // Generate unique filename
            const filename = generateUploadFilename(image.name, user.id);
            const storagePath = filename;

            // Upload to Supabase Storage
            const { data: uploadData, error: uploadError } = await supabase.storage
              .from(CHAT_IMAGES_BUCKET)
              .upload(storagePath, image, {
                cacheControl: '3600',
                upsert: false
              });

            if (uploadError) {
throw uploadError;
}

            // Get public URL
            const { data: urlData } = supabase.storage
              .from(CHAT_IMAGES_BUCKET)
              .getPublicUrl(storagePath);

            const attachment: ImageAttachment = {
              type: 'image',
              url: urlData.publicUrl,
              storage_path: storagePath,
              media_type: image.type as ImageAttachment['media_type'],
              size: image.size,
              filename: image.name,
              uploaded_at: new Date().toISOString()
            };

            imageAttachments.push(attachment);
            imageUrls.push(urlData.publicUrl);

            log.info('Image uploaded', { filename: image.name, url: urlData.publicUrl });
          } catch (error) {
            log.error('Failed to upload image', { filename: image.name }, error as Error);
            // Continue with other images
          }
        }
      }

      // Add user message to UI immediately (optimistic update)
      const tempUserMessage: AIMessage = {
        id: `temp-${Date.now()}`,
        conversation_id: activeId,
        role: 'user',
        content,
        model_used: null,
        tokens_input: null,
        tokens_output: null,
        cost_usd: null,
        response_time_ms: null,
        documents_used: null,
        retrieval_scores: null,
        user_rating: null,
        user_feedback: null,
        flagged: false,
        created_at: new Date().toISOString(),
        metadata: {
          attachments: imageAttachments.length > 0 ? imageAttachments : undefined
        }
      };

      set((state) => ({
        messages: {
          ...state.messages,
          [activeId]: [...(state.messages[activeId] || []), tempUserMessage]
        }
      }));

      // Call Edge Function for AI response
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: {
          message: content,
          conversation_id: activeId,
          image_urls: imageUrls.length > 0 ? imageUrls : undefined,
          image_metadata: imageAttachments.length > 0 ? imageAttachments : undefined
        }
      });

      if (error) {
throw error;
}

      // Replace temp message with real messages from response
      const realMessages = data.messages as AIMessage[];

      set((state) => ({
        messages: {
          ...state.messages,
          [activeId]: [
            ...state.messages[activeId].filter((m) => !m.id.startsWith('temp-')),
            ...realMessages
          ]
        },
        isSending: false
      }));

      // Load referenced documents
      if (data.documents && data.documents.length > 0) {
        const docsMap = data.documents.reduce(
          (acc: Record<string, AIDocument>, doc: AIDocument) => {
            acc[doc.id] = doc;
            return acc;
          },
          {}
        );

        set((state) => ({
          documents: {
            ...state.documents,
            ...docsMap
          }
        }));
      }

      log.info('Message sent successfully', {
        conversationId: activeId,
        imageCount: imageAttachments.length
      });
    } catch (error) {
      log.error('Failed to send message', { conversationId: activeId }, error as Error);

      // Remove optimistic message on error
      const errorMessage = error instanceof Error ? error.message : String(error);
      set((state) => ({
        messages: {
          ...state.messages,
          [activeId]: state.messages[activeId].filter((m) => !m.id.startsWith('temp-'))
        },
        error: {
          code: 'MODEL_ERROR',
          message: `Failed to send message: ${errorMessage}`,
          details: { errorMessage }
        },
        isSending: false
      }));
    }
  },

  // Streaming message support
  sendMessageStreaming: async (content: string, images?: File[], conversationId?: string) => {
    set({ isSending: true, error: null, streamingMessage: null });

    const activeId = conversationId || get().activeConversationId;
    if (!activeId) {
      log.error('No active conversation for streaming');
      set({
        error: { code: 'INVALID_REQUEST', message: 'No active conversation' },
        isSending: false
      });
      return;
    }

    log.info('Sending streaming message', {
      conversationId: activeId,
      contentLength: content.length,
      imageCount: images?.length || 0
    });

    // Timeout ID for SSE stream - declared here so finally block can clear it
    let streamTimeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
throw new Error('Not authenticated');
}

      // Handle image uploads (same as non-streaming)
      const imageAttachments: ImageAttachment[] = [];
      const imageUrls: string[] = [];

      if (images && images.length > 0) {
        for (const image of images) {
          try {
            const filename = generateUploadFilename(image.name, user.id);
            const { error: uploadError } = await supabase.storage
              .from(CHAT_IMAGES_BUCKET)
              .upload(filename, image, { cacheControl: '3600', upsert: false });

            if (uploadError) {
throw uploadError;
}

            const { data: urlData } = supabase.storage
              .from(CHAT_IMAGES_BUCKET)
              .getPublicUrl(filename);

            imageAttachments.push({
              type: 'image',
              url: urlData.publicUrl,
              storage_path: filename,
              media_type: image.type as ImageAttachment['media_type'],
              size: image.size,
              filename: image.name,
              uploaded_at: new Date().toISOString()
            });
            imageUrls.push(urlData.publicUrl);
          } catch (error) {
            log.error('Failed to upload image', { filename: image.name }, error as Error);
          }
        }
      }

      // Initialize streaming message placeholder
      set({
        streamingMessage: {
          id: `streaming-${Date.now()}`,
          conversation_id: activeId,
          content: '',
          isStreaming: true,
          isComplete: false,
          error: null
        }
      });

      // Get session for auth
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
throw new Error('No session');
}

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // Get agent type from active conversation for endpoint routing
      const activeConversation = get().conversations.find((c) => c.id === activeId);
      const agentType = activeConversation?.agent_type || 'general';
      const agentConfig = AGENT_CONFIGS[agentType];
      const endpoint = agentConfig?.endpoint || 'ai-chat';

      // Route to wisr-web-assistant for Wisr agent (non-streaming)
      if (endpoint === 'wisr-web-assistant') {
        // Add optimistic user message to state (will be replaced by server response)
        const tempUserMessage: AIMessage = {
          id: `temp-${Date.now()}`,
          conversation_id: activeId,
          role: 'user',
          content,
          model_used: null,
          tokens_input: null,
          tokens_output: null,
          cost_usd: null,
          response_time_ms: null,
          documents_used: null,
          retrieval_scores: null,
          user_rating: null,
          user_feedback: null,
          flagged: false,
          created_at: new Date().toISOString(),
          metadata: imageAttachments.length > 0 ? { attachments: imageAttachments } : {}
        };

        set((state) => ({
          messages: {
            ...state.messages,
            [activeId]: [...(state.messages[activeId] || []), tempUserMessage]
          }
        }));

        // Call wisr-web-assistant (non-streaming)
        const { data, error } = await supabase.functions.invoke('wisr-web-assistant', {
          body: { message: content, conversation_id: activeId }
        });

        if (error) {
throw error;
}

        // Reload ALL messages from database to capture any messages created during tool execution
        // (e.g., outbound SMS/email messages created by send_sms_to_loan_officer tool)
        const { data: allMessages, error: loadError } = await supabase
          .from('ai_internal_chat_messages')
          .select('*')
          .eq('conversation_id', activeId)
          .order('created_at', { ascending: true });

        if (loadError) {
          log.error('Failed to reload messages after Wisr call', { error: loadError });
        }

        set((state) => ({
          messages: {
            ...state.messages,
            [activeId]: allMessages || []
          },
          streamingMessage: null,
          isSending: false
        }));

        // Update conversation in local state to reflect actual message count
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.id === activeId
              ? {
                  ...c,
                  message_count: allMessages?.length || (c.message_count || 0) + 2,
                  last_message_at: new Date().toISOString(),
                  title: c.title || content.slice(0, 50)
                }
              : c
          )
        }));

        log.info('Wisr message completed', { conversationId: activeId, messageCount: allMessages?.length });
        return;
      }

      // Make streaming request for other agents (ai-chat)
      // Add abort controller with idle-based timeout (resets on each data chunk)
      const abortController = new AbortController();
      const IDLE_TIMEOUT_MS = 60000; // 60 seconds idle timeout (not wall-clock)

      const resetIdleTimeout = () => {
        if (streamTimeoutId) {
clearTimeout(streamTimeoutId);
}
        streamTimeoutId = setTimeout(() => {
          log.warn('Stream idle timeout - no data for 60s, aborting', { conversationId: activeId });
          abortController.abort();
        }, IDLE_TIMEOUT_MS);
      };

      resetIdleTimeout(); // Start initial timeout

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          message: content,
          conversation_id: activeId,
          stream: true,
          image_urls: imageUrls.length > 0 ? imageUrls : undefined,
          image_metadata: imageAttachments.length > 0 ? imageAttachments : undefined
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let documents: SearchResult[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
break;
}

        // Reset idle timeout on each chunk received
        resetIdleTimeout();

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) {
continue;
}

          try {
            const data = JSON.parse(line.slice(6));

            switch (data.type) {
              case 'user_message':
                // Add user message to state (skip if realtime already added it)
                set((state) => {
                  const existing = state.messages[activeId] || [];
                  if (existing.some(m => m.id === data.message.id)) {
                    return state; // Already added by realtime
                  }
                  return {
                    messages: {
                      ...state.messages,
                      [activeId]: [...existing, data.message]
                    }
                  };
                });
                break;

              case 'sources':
                documents = data.documents;
                break;

              case 'delta':
                // Append text to streaming message
                set((state) => ({
                  streamingMessage: state.streamingMessage
                    ? { ...state.streamingMessage, content: state.streamingMessage.content + data.text }
                    : null
                }));
                break;

              case 'done':
                // Add final assistant message and clear streaming (skip if realtime already added it)
                set((state) => {
                  const existing = state.messages[activeId] || [];
                  const alreadyAdded = existing.some(m => m.id === data.message.id);
                  const newDocuments = documents.reduce(
                    (acc, doc) => {
                      acc[doc.chunk_id] = doc as unknown as AIDocument;
                      return acc;
                    },
                    { ...state.documents }
                  );

                  if (alreadyAdded) {
                    // Message already added by realtime, just clear streaming state
                    return {
                      streamingMessage: null,
                      isSending: false,
                      documents: newDocuments
                    };
                  }
                  return {
                    messages: {
                      ...state.messages,
                      [activeId]: [...existing, data.message]
                    },
                    streamingMessage: null,
                    isSending: false,
                    documents: newDocuments
                  };
                });
                break;

              case 'error':
                set({
                  streamingMessage: null,
                  error: { code: 'MODEL_ERROR', message: data.message },
                  isSending: false
                });
                break;
            }
          } catch (parseError) {
            log.warn('Failed to parse SSE event', { line });
          }
        }
      }

      // Flush any remaining buffer content (handles case where done event is in final chunk)
      if (buffer.trim() && buffer.startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.slice(6));
          if (data.type === 'done' && data.message) {
            set((state) => {
              const existing = state.messages[activeId] || [];
              const alreadyAdded = existing.some(m => m.id === data.message.id);
              const newDocuments = documents.reduce(
                (acc, doc) => {
                  acc[doc.chunk_id] = doc as unknown as AIDocument;
                  return acc;
                },
                { ...state.documents }
              );

              if (alreadyAdded) {
                return {
                  streamingMessage: null,
                  isSending: false,
                  documents: newDocuments
                };
              }
              return {
                messages: {
                  ...state.messages,
                  [activeId]: [...existing, data.message]
                },
                streamingMessage: null,
                isSending: false,
                documents: newDocuments
              };
            });
          }
        } catch (e) {
          log.debug('Failed to parse remaining buffer', { buffer: buffer.slice(0, 100) });
        }
      }

      log.info('Streaming message completed', { conversationId: activeId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Streaming failed', { conversationId: activeId, errorMessage }, error as Error);
      set({
        streamingMessage: null,
        error: { code: 'MODEL_ERROR', message: `Failed to stream message: ${errorMessage}`, details: { errorMessage } },
        isSending: false
      });
    } finally {
      // Clear the stream timeout if it was set
      if (streamTimeoutId) {
        clearTimeout(streamTimeoutId);
      }
      // Ensure isSending is always reset
      set((state) => (
        state.isSending
          ? { ...state, isSending: false, streamingMessage: null }
          : state
      ));
    }
  },

  rateMessage: async (messageId: string, rating: number, feedback?: string) => {
    log.info('Rating message', { messageId, rating });

    try {
      const { error } = await supabase
        .from('ai_internal_chat_messages')
        .update({
          user_rating: rating,
          user_feedback: feedback || null
        })
        .eq('id', messageId);

      if (error) {
throw error;
}

      // Update in state
      set((state) => {
        const newMessages = { ...state.messages };
        Object.keys(newMessages).forEach((convId) => {
          newMessages[convId] = newMessages[convId].map((msg) =>
            msg.id === messageId
              ? { ...msg, user_rating: rating, user_feedback: feedback || null }
              : msg
          );
        });
        return { messages: newMessages };
      });

      log.info('Message rated', { messageId, rating });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Failed to rate message', { messageId, errorMessage }, error as Error);
      set({
        error: {
          code: 'DATABASE_ERROR',
          message: `Failed to rate message: ${errorMessage}`,
          details: { errorMessage }
        }
      });
    }
  },

  flagMessage: async (messageId: string, reason: string) => {
    log.info('Flagging message', { messageId, reason });

    try {
      const { error } = await supabase
        .from('ai_internal_chat_messages')
        .update({
          flagged: true,
          user_feedback: reason
        })
        .eq('id', messageId);

      if (error) {
throw error;
}

      // Update in state
      set((state) => {
        const newMessages = { ...state.messages };
        Object.keys(newMessages).forEach((convId) => {
          newMessages[convId] = newMessages[convId].map((msg) =>
            msg.id === messageId ? { ...msg, flagged: true, user_feedback: reason } : msg
          );
        });
        return { messages: newMessages };
      });

      log.info('Message flagged', { messageId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Failed to flag message', { messageId, errorMessage }, error as Error);
      set({
        error: {
          code: 'DATABASE_ERROR',
          message: `Failed to flag message: ${errorMessage}`,
          details: { errorMessage }
        }
      });
    }
  },

  regenerateMessage: async (messageId: string) => {
    const state = get();
    log.info('Regenerating message', { messageId });

    // Find the message and its conversation
    let targetConversationId: string | null = null;
    let targetMessage: AIMessage | null = null;
    let userMessage: AIMessage | null = null;

    for (const [convId, msgs] of Object.entries(state.messages)) {
      const msgIndex = msgs.findIndex((m) => m.id === messageId);
      if (msgIndex !== -1) {
        targetConversationId = convId;
        targetMessage = msgs[msgIndex];

        // Find the preceding user message to resend
        for (let i = msgIndex - 1; i >= 0; i--) {
          if (msgs[i].role === 'user') {
            userMessage = msgs[i];
            break;
          }
        }
        break;
      }
    }

    if (!targetConversationId || !targetMessage) {
      log.error('Message not found for regeneration', { messageId });
      set({
        error: { code: 'INVALID_REQUEST', message: 'Message not found' }
      });
      return;
    }

    if (!userMessage) {
      log.error('No user message found to regenerate from', { messageId });
      set({
        error: { code: 'INVALID_REQUEST', message: 'No user message found to regenerate from' }
      });
      return;
    }

    // Check if Wisr agent - don't allow regeneration due to side effects
    const conversation = state.conversations.find((c) => c.id === targetConversationId);
    if (conversation?.agent_type === 'wisr') {
      log.warn('Cannot regenerate Wisr messages - has side effects');
      set({
        error: { code: 'INVALID_REQUEST', message: 'Cannot regenerate Wisr messages - they may have side effects (SMS, email)' }
      });
      return;
    }

    try {
      // Delete the assistant message from database
      const { error: deleteError } = await supabase
        .from('ai_internal_chat_messages')
        .delete()
        .eq('id', messageId);

      if (deleteError) {
throw deleteError;
}

      // Remove from local state
      set((state) => ({
        messages: {
          ...state.messages,
          [targetConversationId!]: state.messages[targetConversationId!].filter(
            (m) => m.id !== messageId
          )
        }
      }));

      // Get image attachments from original user message if any
      const imageAttachments = userMessage.metadata?.attachments || [];
      const imageUrls = imageAttachments
        .filter((a): a is ImageAttachment => a.type === 'image')
        .map((a) => a.url);

      // Resend the user message content to get a new response
      // Use streaming version to provide real-time feedback
      log.info('Resending user message for regeneration', {
        conversationId: targetConversationId,
        userMessageId: userMessage.id,
        hasImages: imageUrls.length > 0
      });

      // Delete the user message too since sendMessageStreaming will recreate it
      const { error: deleteUserError } = await supabase
        .from('ai_internal_chat_messages')
        .delete()
        .eq('id', userMessage.id);

      if (deleteUserError) {
throw deleteUserError;
}

      // Remove user message from local state
      set((state) => ({
        messages: {
          ...state.messages,
          [targetConversationId!]: state.messages[targetConversationId!].filter(
            (m) => m.id !== userMessage!.id
          )
        }
      }));

      // Call sendMessageStreaming with the original content
      // Note: We can't pass File objects since we only have URLs, but the edge function
      // should handle the image_urls parameter for existing images
      await get().sendMessageStreaming(userMessage.content, undefined, targetConversationId);

      log.info('Regeneration complete', { messageId, conversationId: targetConversationId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Failed to regenerate message', { messageId, errorMessage }, error as Error);
      set({
        error: {
          code: 'MODEL_ERROR',
          message: `Failed to regenerate message: ${errorMessage}`,
          details: { errorMessage }
        }
      });
    }
  },

  // ============================================================================
  // Actions - Documents
  // ============================================================================

  loadDocument: async (documentId: string) => {
    log.debug('Loading document', { documentId });

    try {
      const { data: document, error } = await supabase
        .from('ai_documents')
        .select('*')
        .eq('id', documentId)
        .single();

      if (error) {
throw error;
}

      // Store in state
      set((state) => ({
        documents: {
          ...state.documents,
          [documentId]: document
        }
      }));

      return document;
    } catch (error) {
      log.error('Failed to load document', { documentId }, error as Error);
      throw error;
    }
  },

  // ============================================================================
  // Actions - Utility
  // ============================================================================

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    log.info('Resetting chat store');

    // Clean up Realtime subscription
    if (activeRealtimeChannel) {
      supabase.removeChannel(activeRealtimeChannel);
      activeRealtimeChannel = null;
    }
    subscribedConversationId = null;

    set({
      conversations: [],
      activeConversationId: null,
      isLoadingConversations: false,
      messages: {},
      isLoadingMessages: false,
      streamingMessage: null,
      isSending: false,
      error: null,
      documents: {}
    });
  }
}));

// ============================================================================
// Selectors (for performance optimization)
// ============================================================================

// Empty array constant to avoid creating new references
const EMPTY_ARRAY: AIMessage[] = [];

// Get active conversation - returns the conversation object or null
export const useActiveConversation = (): AIConversation | null => {
  const activeId = useChatStore((state) => state.activeConversationId);
  const conversations = useChatStore((state) => state.conversations, shallow);

  if (!activeId) {
return null;
}
  return conversations.find((c) => c.id === activeId) ?? null;
};

// Get messages for active conversation - returns array of messages
// Simplified change detection: use shallow comparison on the array reference
export const useActiveMessages = (): AIMessage[] => {
  const activeId = useChatStore((state) => state.activeConversationId);
  // Use shallow comparison - relies on immutable updates creating new array refs
  const conversationMessages = useChatStore(
    (state) => (activeId ? state.messages[activeId] : null),
    shallow
  );

  if (!activeId || !conversationMessages) {
return EMPTY_ARRAY;
}
  return conversationMessages;
};

// Get documents for current messages
export const useMessageDocuments = (documentIds: string[]): AIDocument[] =>
  useChatStore(
    (state) => documentIds.map((id) => state.documents[id]).filter(Boolean) as AIDocument[],
    shallow
  );
